// frontend/packages/business-console/src/pages/Invoices/useInvoicesList.js
//
// Data hook for the Invoices list page. Mirrors `useQuotesList`
// against `/api/v2/crm/invoices`.
//
// Two modes:
//   - Demo (`?demo=1` or `localStorage["pulse.demo"]==="1"`): returns
//     handcrafted fixtures from `./demoData.js`. Filter + search apply
//     on the client-side fixtures so the UI behaves identically to
//     real-data mode.
//   - Real (default): calls `/api/v2/crm/invoices` via `listInvoices`.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { listInvoices } from '../../services/invoiceService';
import { demoInvoicesList } from './demoData';

function readDemoFlag(searchString) {
  const params = new URLSearchParams(searchString || '');
  if (params.get('demo') === '1') return true;
  try {
    return localStorage.getItem('pulse.demo') === '1';
  } catch {
    return false;
  }
}

function filterDemoItems(items, { state, q }) {
  let out = items;
  if (state && state !== 'all') {
    out = out.filter((x) => x.state === state);
  }
  if (q && q.trim()) {
    const needle = q.trim().toLowerCase();
    out = out.filter(
      (x) =>
        x.number.toLowerCase().includes(needle) ||
        (x.customerName || '').toLowerCase().includes(needle)
    );
  }
  return out;
}

/**
 * Aggregate outstanding-amount summary for the chip strip above the
 * table. Computed from the currently-displayed list so it reflects
 * the active filter (e.g. "Overdue" filter shows just the overdue
 * sub-totals). Cheap O(n) on the page.
 */
export function summarizeInvoices(items) {
  let outstandingCents = 0;
  let outstandingCount = 0;
  let overdueCents = 0;
  let overdueCount = 0;
  for (const inv of items) {
    if (['sent', 'viewed', 'partial', 'overdue'].includes(inv.state)) {
      outstandingCents += inv.amountDue || 0;
      outstandingCount += 1;
    }
    if (inv.state === 'overdue') {
      overdueCents += inv.amountDue || 0;
      overdueCount += 1;
    }
  }
  return { outstandingCents, outstandingCount, overdueCents, overdueCount };
}

export function useInvoicesList({ state, q } = {}) {
  const location = useLocation();
  const isDemo = useMemo(
    () => readDemoFlag(location.search),
    [location.search]
  );

  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isDemo) {
        const filtered = filterDemoItems(demoInvoicesList.items, { state, q });
        setItems(filtered);
        setHasMore(false);
        setNextCursor(null);
      } else {
        const params = {};
        if (state && state !== 'all') params.state = state;
        if (q && q.trim()) params.q = q.trim();
        const res = await listInvoices(params);
        setItems(res.items || []);
        setHasMore(Boolean(res.hasMore));
        setNextCursor(res.nextCursor || null);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [isDemo, state, q]);

  const loadMore = useCallback(async () => {
    if (!hasMore || !nextCursor || isDemo) return;
    setLoadingMore(true);
    try {
      const params = { before: nextCursor };
      if (state && state !== 'all') params.state = state;
      if (q && q.trim()) params.q = q.trim();
      const res = await listInvoices(params);
      setItems((prev) => [...prev, ...(res.items || [])]);
      setHasMore(Boolean(res.hasMore));
      setNextCursor(res.nextCursor || null);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, nextCursor, isDemo, state, q]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  return {
    items,
    hasMore,
    nextCursor,
    loading,
    loadingMore,
    error,
    loadMore,
    refresh: loadFirstPage,
    isDemo,
  };
}
